import { c as _c } from "react/compiler-runtime";
import { standardSchemaResolver as zodResolver } from '@hookform/resolvers/standard-schema';
import { useUnmountEffect } from '@react-hookz/web';
import { useMutation } from '@tanstack/react-query';
import { get } from 'es-toolkit/compat';
import { atom, useAtom } from 'jotai';
import { ofetch } from 'ofetch';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod/v4-mini';
import type { Login } from '@/app/[locale]/subtitles/_api/os';
import { osSessionAtom } from '@/app/[locale]/subtitles/_api/os';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { env } from '@/env';
import { bindApply } from '@sub-vocab/utils/lib';
const osAuthAtom = atom({
  username: '',
  password: ''
});
export function OpensubtitlesAuthentication() {
  const $ = _c(45);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [osAuth, setOsAuth] = useAtom(osAuthAtom);
  const [osSession, setOsSession] = useAtom(osSessionAtom);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationKey: ["subtitles-login"],
      mutationFn: bindApply(ofetch)
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    mutateAsync,
    isPending
  } = useMutation(t0);
  const formDefaultValues = osAuth;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = zodResolver(z.object({
      username: z.string().check(z.minLength(1, {
        error: "Username is required"
      })),
      password: z.string().check(z.minLength(1, {
        error: "Password is required"
      }))
    }));
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== formDefaultValues) {
    t2 = {
      defaultValues: formDefaultValues,
      mode: "onBlur",
      resolver: t1
    };
    $[2] = formDefaultValues;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const form = useForm(t2);
  const {
    handleSubmit,
    formState: t3,
    setError
  } = form;
  const {
    errors
  } = t3;
  let t4;
  if ($[4] !== mutateAsync || $[5] !== setError || $[6] !== setOsSession) {
    t4 = async function onSubmit(values) {
      setPasswordVisible(false);
      const baseUrl = `${env.NEXT_PUBLIC_SUB_API_URL}/opensubtitles-proxy/def`;
      const result = await mutateAsync([`${baseUrl}/login`, {
        method: "POST",
        body: values satisfies Login['Body']
      }]).catch(e => {
        const message = get(e, "data.message") ?? get(e, "message") ?? "Error";
        setError("root.serverError", {
          message
        });
      });
      if (result) {
        setOsSession(result);
      }
    };
    $[4] = mutateAsync;
    $[5] = setError;
    $[6] = setOsSession;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const onSubmit = t4;
  let t5;
  if ($[8] !== form || $[9] !== setOsAuth) {
    t5 = () => {
      setOsAuth(form.getValues());
    };
    $[8] = form;
    $[9] = setOsAuth;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  useUnmountEffect(t5);
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="space-y-2"><h4 className="leading-none font-medium">Sign In to{" "}<a href="https://www.opensubtitles.com/users/sign_up" target="_blank" rel="noreferrer noopener" className="underline">OpenSubtitles</a></h4></div>;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== handleSubmit || $[13] !== onSubmit) {
    t7 = handleSubmit(onSubmit);
    $[12] = handleSubmit;
    $[13] = onSubmit;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== errors.username?.message) {
    t8 = t9 => {
      const {
        field
      } = t9;
      return <FormItem><FormLabel>Username</FormLabel><FormControl><div><Input type="text" autoComplete="username" {...field} className="text-base md:text-sm" /></div></FormControl><FormMessage>{errors.username?.message ?? ""}</FormMessage></FormItem>;
    };
    $[15] = errors.username?.message;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== form.control || $[18] !== t8) {
    t9 = <FormField control={form.control} name="username" render={t8} />;
    $[17] = form.control;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== errors.password?.message || $[21] !== passwordVisible) {
    t10 = t11 => {
      const {
        field: field_0
      } = t11;
      return <FormItem><FormLabel className="inline-flex w-full text-sm"><span className="grow">Password</span></FormLabel><FormControl><div className="flex items-center gap-1"><div className="grow"><Input type={passwordVisible ? "text" : "password"} autoComplete="current-password" {...field_0} className="text-base md:text-sm" /></div><Button variant="outline" className="px-2!" aria-checked={passwordVisible} onClick={e_0 => {
              e_0.preventDefault();
              setPasswordVisible(!passwordVisible);
            }}>{passwordVisible ? <svg className="icon-[lucide--eye] size-4.5 text-neutral-600" /> : <svg className="icon-[lucide--eye-off] size-4.5 text-neutral-600" />}</Button></div></FormControl><FormMessage>{errors.password?.message ?? ""}</FormMessage></FormItem>;
    };
    $[20] = errors.password?.message;
    $[21] = passwordVisible;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== form.control || $[24] !== t10) {
    t11 = <FormField control={form.control} name="password" render={t10} />;
    $[23] = form.control;
    $[24] = t10;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const t12 = errors.root?.serverError?.message;
  let t13;
  if ($[26] !== t12) {
    t13 = <FormMessage className="break-words">{t12}</FormMessage>;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <svg className="icon-[lucide--loader-2] hidden animate-spin group-disabled:block" />;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== isPending) {
    t15 = <Button className="group gap-1.5" type="submit" disabled={isPending}>Get Token{t14}</Button>;
    $[29] = isPending;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t11 || $[32] !== t13 || $[33] !== t15 || $[34] !== t7 || $[35] !== t9) {
    t16 = <form onSubmit={t7} className="space-y-4">{t9}{t11}{t13}{t15}</form>;
    $[31] = t11;
    $[32] = t13;
    $[33] = t15;
    $[34] = t7;
    $[35] = t9;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== form || $[38] !== t16) {
    t17 = <div className="grid gap-2"><Form {...form}>{t16}</Form></div>;
    $[37] = form;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== osSession) {
    t18 = osSession?.token ? <div className="overflow-x-scroll"><span>{osSession.token}</span></div> : null;
    $[40] = osSession;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== t17 || $[43] !== t18) {
    t19 = <div className="grid gap-4">{t6}{t17}{t18}</div>;
    $[42] = t17;
    $[43] = t18;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  return t19;
}